<script>
    import { getYearsDifferFrom } from "../../../Utils/utilFunctions.svelte";
    import TimePicker from "./TimePicker.svelte";
    import { getMonthLength, getCalendarDays } from "./date-utils.js";
    import { getInnerLocale } from "./locale.js";
    import { createEventDispatcher } from "svelte";
    const dispatch = createEventDispatcher();
    function cloneDate(d) {
        return new Date(d.getTime());
    }
    export let showYearDropdown = true;
    export let value = null;
    function setValue(d) {
        if (d.getTime() !== value?.getTime()) {
            browseDate = clamp(d, min, max);
            value = cloneDate(browseDate);
        }
    }
    function browse(d) {
        browseDate = clamp(d, min, max);
        if (!browseWithoutSelecting && value) {
            setValue(browseDate);
        }
    }
    const todayDate = /* @__PURE__ */ new Date();
    const defaultDate = /* @__PURE__ */ new Date();
    export let timePrecision = null;
    export let min = new Date(defaultDate.getFullYear() - 20, 0, 1);
    export let max = new Date(
        defaultDate.getFullYear(),
        11,
        31,
        23,
        59,
        59,
        999,
    );
    export let minYear = new Date("2019-01-01"),
        maxYear = new Date(getYearsDifferFrom(5, new Date())),
        showScrollYearOptions = false,
        yearOptionSize;
    const defaultYearOptionSize = 12;
    $: displayYearOptionSize = yearOptionSize ?? defaultYearOptionSize;
    export let titleText = "",
        titleStyle = "";
    $: if (value && value > max) {
        setValue(max);
    } else if (value && value < min) {
        setValue(min);
    }
    function clamp(d, min2, max2) {
        if (d > max2) {
            return cloneDate(max2);
        } else if (d < min2) {
            return cloneDate(min2);
        } else {
            return cloneDate(d);
        }
    }
    let browseDate = value
        ? cloneDate(value)
        : cloneDate(clamp(defaultDate, min, max));
    $: setBrowseDate(value);
    function setBrowseDate(value2) {
        if (browseDate.getTime() !== value2?.getTime()) {
            browseDate = value2 ? cloneDate(value2) : browseDate;
        }
    }
    let years = getYears(
        showScrollYearOptions ? (minYear ?? min) : min,
        showScrollYearOptions ? (maxYear ?? max) : max,
    );
    $: years = getYears(
        showScrollYearOptions ? (minYear ?? min) : min,
        showScrollYearOptions ? (maxYear ?? max) : max,
    );
    function getYears(min2, max2) {
        let years2 = [];
        for (let i = min2.getFullYear(); i <= max2.getFullYear(); i++) {
            years2.push(i);
        }
        return years2;
    }
    export let locale = {};
    let calendarDivRef;
    $: iLocale = getInnerLocale(locale);
    export let browseWithoutSelecting = false;
    $: browseYear = browseDate.getFullYear();
    function setYear(newYear) {
        browseDate.setFullYear(newYear);
        browse(browseDate);
    }
    $: browseMonth = browseDate.getMonth();
    function setMonth(newMonth) {
        let newYear = browseDate.getFullYear();
        if (newMonth === 12) {
            newMonth = 0;
            newYear++;
        } else if (newMonth === -1) {
            newMonth = 11;
            newYear--;
        }
        const maxDate = getMonthLength(newYear, newMonth);
        const newDate = Math.min(browseDate.getDate(), maxDate);
        browse(
            new Date(
                newYear,
                newMonth,
                newDate,
                browseDate.getHours(),
                browseDate.getMinutes(),
                browseDate.getSeconds(),
                browseDate.getMilliseconds(),
            ),
        );
    }
    $: calendarDays = getCalendarDays(browseDate, iLocale.weekStartsOn);
    function selectDay(calendarDay) {
        if (dayIsInRange(calendarDay, min, max)) {
            browseDate.setFullYear(0);
            browseDate.setMonth(0);
            browseDate.setDate(1);
            browseDate.setFullYear(calendarDay.year);
            browseDate.setMonth(calendarDay.month);
            browseDate.setDate(calendarDay.number);
            setValue(browseDate);
            dispatch("select", cloneDate(browseDate));
        }
    }
    function dayIsInRange(calendarDay, min2, max2) {
        const date = new Date(
            calendarDay.year,
            calendarDay.month,
            calendarDay.number,
        );
        const minDate = new Date(
            min2.getFullYear(),
            min2.getMonth(),
            min2.getDate(),
        );
        const maxDate = new Date(
            max2.getFullYear(),
            max2.getMonth(),
            max2.getDate(),
        );
        return date >= minDate && date <= maxDate;
    }
    function shiftKeydown(e) {
        if (e.shiftKey && e.key === "ArrowUp") {
            setYear(browseDate.getFullYear() - 1);
        } else if (e.shiftKey && e.key === "ArrowDown") {
            setYear(browseDate.getFullYear() + 1);
        } else if (e.shiftKey && e.key === "ArrowLeft") {
            setMonth(browseDate.getMonth() - 1);
        } else if (e.shiftKey && e.key === "ArrowRight") {
            setMonth(browseDate.getMonth() + 1);
        } else {
            return false;
        }
        e.preventDefault();
        return true;
    }
    function yearKeydown(e) {
        let shift = e.shiftKey || e.altKey;
        if (shift) {
            shiftKeydown(e);
            return;
        } else if (e.key === "ArrowUp") {
            setYear(browseDate.getFullYear() - 1);
        } else if (e.key === "ArrowDown") {
            setYear(browseDate.getFullYear() + 1);
        } else if (e.key === "ArrowLeft") {
            setMonth(browseDate.getMonth() - 1);
        } else if (e.key === "ArrowRight") {
            setMonth(browseDate.getMonth() + 1);
        } else {
            shiftKeydown(e);
            return;
        }
        e.preventDefault();
    }
    function monthKeydown(e) {
        let shift = e.shiftKey || e.altKey;
        if (shift) {
            shiftKeydown(e);
            return;
        } else if (e.key === "ArrowUp" || e.key === "ArrowLeft") {
            setMonth(browseDate.getMonth() - 1);
        } else if (e.key === "ArrowDown" || e.key === "ArrowRight") {
            setMonth(browseDate.getMonth() + 1);
        } else {
            shiftKeydown(e);
            return;
        }
        e.preventDefault();
    }
    function keydown(e) {
        let shift = e.shiftKey || e.altKey;
        if (e.target?.tagName === "SELECT" || e.target?.tagName === "SPAN") {
            return;
        }
        if (shift) {
            shiftKeydown(e);
            return;
        } else if (e.key === "ArrowUp") {
            browseDate.setDate(browseDate.getDate() - 7);
            setValue(browseDate);
        } else if (e.key === "ArrowDown") {
            browseDate.setDate(browseDate.getDate() + 7);
            setValue(browseDate);
        } else if (e.key === "ArrowLeft") {
            browseDate.setDate(browseDate.getDate() - 1);
            setValue(browseDate);
        } else if (e.key === "ArrowRight") {
            browseDate.setDate(browseDate.getDate() + 1);
            setValue(browseDate);
        } else if (e.key === "Enter") {
            setValue(browseDate);
            dispatch("select", cloneDate(browseDate));
        } else {
            return;
        }
        e.preventDefault();
    }
</script>

<!-- svelte-ignore a11y-no-noninteractive-tabindex -->
<!-- svelte-ignore a11y-no-static-element-interactions -->
<div class="date-time-picker" on:focusout tabindex="0" on:keydown={keydown}>
    <div class="tab-container" tabindex="-1">
        {#if titleText}
            <div class="w-full text-left underline {titleStyle}">
                {titleText}
            </div>
        {/if}
        <div class="top">
            <button
                type="button"
                class="page-button"
                tabindex="-1"
                on:click={() => setMonth(browseDate.getMonth() - 1)}
            >
                <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    ><path
                        d="M5 3l3.057-3 11.943 12-11.943 12-3.057-3 9-9z"
                        transform="rotate(180, 12, 12)"
                    /></svg
                >
            </button>
            <div class="dropdown month">
                <select
                    value={browseMonth}
                    on:keydown={monthKeydown}
                    on:input={(e) => setMonth(parseInt(e.currentTarget.value))}
                >
                    {#each iLocale.months as monthName, i}
                        <option
                            disabled={new Date(
                                browseYear,
                                i,
                                getMonthLength(browseYear, i),
                                23,
                                59,
                                59,
                                999,
                            ) < min || new Date(browseYear, i) > max}
                            value={i}>{monthName}</option
                        >
                    {/each}
                </select>
                <!--
                        Here we have use `select.dummy-select` for showing just the <select> button. This
                        is to style the <select> button without affecting the menu popup
                        - `option { color: initial }` causes invisible menu in dark mode on Firefox
                        - `option { color: initial; background-color: initial }` causes invisible menu in Chrome
                        - `select { background-color: $bg; color: $text }` causes white scrollbar in dark mode on Firefox
                    -->
                <select class="dummy-select" tabindex="-1">
                    {#each iLocale.months as monthName, i}
                        <option value={i} selected={i === browseMonth}
                            >{monthName}</option
                        >
                    {/each}
                </select>
                <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    ><path
                        d="M6 0l12 12-12 12z"
                        transform="rotate(90, 12, 12)"
                    /></svg
                >
            </div>

            {#if showYearDropdown}
                <div
                    class="dropdown year"
                    style="width: auto; min-width: 100px; max-width: 150px; height: 25px;"
                >
                    <select
                        style="position: absolute; width: auto; max-width: 150px; min-width: 100px; height: 25px; overflow-y: auto; opacity: 1; line-height: 25px; pointer-events: auto; z-index: 1;"
                        value={browseYear}
                        bind:this={calendarDivRef}
                        on:input={(e) =>
                            setYear(parseInt(e.currentTarget.value))}
                        on:keydown={yearKeydown}
                        on:focus|preventDefault={(e) => {
                            if (!showScrollYearOptions) return;
                            e.target.size = Math.min(
                                years.length,
                                displayYearOptionSize,
                            );
                            e.target.style.top = "25px";
                            e.target.style.height = "auto";

                            const index = years.indexOf(browseYear);
                            if (index !== -1) {
                                e.target.scrollTop =
                                    index * e.target.options[0].offsetHeight;
                            }
                        }}
                        on:blur={(e) => {
                            e.target.size = 0;
                            e.target.style.top = "0";
                            e.target.style.height = "25px";
                        }}
                        on:change|preventDefault={(e) => {
                            e.target.size = 0;
                            e.target.style.top = "0";
                            e.target.style.height = "25px";
                        }}
                        on:mousedown={(e) => {
                            if (
                                e.target.tagName === "SELECT" &&
                                e.target.size == 0
                            )
                                setTimeout(() => {
                                    e.target.size = Math.min(
                                        years.length,
                                        displayYearOptionSize,
                                    );
                                    e.target.style.top = "25px";
                                    e.target.style.height = "auto";

                                    const index = years.indexOf(browseYear);
                                    if (index !== -1) {
                                        e.target.scrollTop =
                                            index *
                                            e.target.options[0].offsetHeight;
                                    }
                                }, 0);
                        }}
                    >
                        {#each years as v}
                            <option value={v} style="padding: 2px;">
                                {v}
                            </option>
                        {/each}
                    </select>
                    <!-- style <select> button without affecting menu popup -->
                    <select
                        class="dummy-select absolute w-auto max-w-[150px] h-[25px] min-w-[100px]"
                        tabindex="-1"
                    >
                        {#each years as v}
                            <option
                                value={v}
                                selected={v === browseDate.getFullYear()}
                            >
                                {v}
                            </option>
                        {/each}
                    </select>
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        ><path
                            d="M6 0l12 12-12 12z"
                            transform="rotate(90, 12, 12)"
                        /></svg
                    >
                </div>
            {/if}
            <button
                type="button"
                class="page-button"
                tabindex="-1"
                on:click={() => setMonth(browseDate.getMonth() + 1)}
            >
                <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    ><path
                        d="M5 3l3.057-3 11.943 12-11.943 12-3.057-3 9-9z"
                    /></svg
                >
            </button>
        </div>
        <div class="header">
            <!-- eslint-disable-next-line @typescript-eslint/no-unused-vars -->
            {#each Array(7) as _, i}
                {#if i + iLocale.weekStartsOn < 7}
                    <div class="header-cell">
                        {iLocale.weekdays[iLocale.weekStartsOn + i]}
                    </div>
                {:else}
                    <div class="header-cell">
                        {iLocale.weekdays[iLocale.weekStartsOn + i - 7]}
                    </div>
                {/if}
            {/each}
        </div>
        <!-- eslint-disable-next-line @typescript-eslint/no-unused-vars -->
        {#each Array(6) as _, weekIndex}
            <div class="week">
                {#each calendarDays.slice(weekIndex * 7, weekIndex * 7 + 7) as calendarDay}
                    <!-- svelte-ignore a11y-click-events-have-key-events -->
                    <div
                        class="cell"
                        on:click={() => selectDay(calendarDay)}
                        class:disabled={!dayIsInRange(calendarDay, min, max)}
                        class:selected={value &&
                            calendarDay.year === value.getFullYear() &&
                            calendarDay.month === value.getMonth() &&
                            calendarDay.number === value.getDate()}
                        class:today={calendarDay.year ===
                            todayDate.getFullYear() &&
                            calendarDay.month === todayDate.getMonth() &&
                            calendarDay.number === todayDate.getDate()}
                        class:other-month={calendarDay.month !== browseMonth}
                    >
                        <span>{calendarDay.number}</span>
                    </div>
                {/each}
            </div>
        {/each}

        <TimePicker {timePrecision} {browseDate} {browse} />
    </div>
</div>

<style>
    .date-time-picker {
        display: inline-block;
        color: var(--date-picker-foreground, #000000);
        background: var(--date-picker-background, #ffffff);
        user-select: none;
        -webkit-user-select: none;
        padding: 0.5rem;
        cursor: default;
        font-size: 0.75rem;
        border: 1px solid rgba(103, 113, 137, 0.3);
        border-radius: 3px;
        box-shadow:
            0px 2px 6px rgba(0, 0, 0, 0.08),
            0px 2px 6px rgba(0, 0, 0, 0.11);
        outline: none;
        transition: all 80ms cubic-bezier(0.4, 0, 0.2, 1);
    }
    .date-time-picker:focus {
        border-color: var(--date-picker-highlight-border, #0269f7);
        box-shadow: 0px 0px 0px 2px
            var(--date-picker-highlight-shadow, rgba(2, 105, 247, 0.4));
    }

    .tab-container {
        outline: none;
    }

    .top {
        display: flex;
        justify-content: center;
        align-items: center;
        padding-bottom: 0.5rem;
    }

    .dropdown {
        margin-left: 0.25rem;
        margin-right: 0.25rem;
        position: relative;
        display: flex;
    }
    .dropdown svg {
        position: absolute;
        right: 0px;
        top: 0px;
        height: 100%;
        width: 8px;
        padding: 0rem 0.5rem;
        pointer-events: none;
        box-sizing: content-box;
    }

    .month {
        flex-grow: 1;
    }

    .year {
        flex-grow: 1;
    }

    svg {
        display: block;
        fill: var(--date-picker-foreground, #000000);
        opacity: 0.75;
        outline: none;
        z-index: 2;
    }

    .page-button {
        background-color: transparent;
        width: 1.5rem;
        height: 1.5rem;
        flex-shrink: 0;
        border-radius: 5px;
        box-sizing: border-box;
        border: 1px solid transparent;
        display: flex;
        align-items: center;
        justify-content: center;
    }
    .page-button:hover {
        background-color: rgba(128, 128, 128, 0.08);
        border: 1px solid rgba(128, 128, 128, 0.08);
    }
    .page-button svg {
        width: 0.68rem;
        height: 0.68rem;
    }

    select.dummy-select {
        position: absolute;
        width: 100%;
        pointer-events: none;
        outline: none;
        color: var(--date-picker-foreground, #000000);
        background-color: var(--date-picker-background, #ffffff);
        border-radius: 3px;
    }

    select:focus + select.dummy-select {
        border-color: var(--date-picker-highlight-border, #0269f7);
        box-shadow: 0px 0px 0px 2px
            var(--date-picker-highlight-shadow, rgba(2, 105, 247, 0.4));
    }

    select:not(.dummy-select) {
        opacity: 0;
    }

    select {
        font-size: inherit;
        font-family: inherit;
        -webkit-appearance: none;
        -moz-appearance: none;
        /* appearance: none; */
        flex-grow: 1;
        padding: 0rem 0.35rem;
        height: 1.5rem;
        padding-right: 1.3rem;
        margin: 0px;
        border: 1px solid rgba(108, 120, 147, 0.3);
        outline: none;
        transition: all 80ms cubic-bezier(0.4, 0, 0.2, 1);
        background-image: none;
        appearance: auto;
    }

    .header {
        display: flex;
        font-weight: 600;
        padding-bottom: 2px;
    }

    .header-cell {
        width: 1.875rem;
        text-align: center;
        flex-grow: 1;
    }

    .week {
        display: flex;
    }

    .cell {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 2rem;
        height: 1.94rem;
        flex-grow: 1;
        border-radius: 5px;
        box-sizing: border-box;
        border: 2px solid transparent;
    }
    .cell:hover {
        border: 1px solid rgba(128, 128, 128, 0.08);
    }
    .cell.today {
        font-weight: 600;
        border: 2px solid
            var(--date-picker-today-border, rgba(128, 128, 128, 0.3));
    }
    .cell:hover {
        background-color: rgba(128, 128, 128, 0.08);
    }
    .cell.disabled {
        visibility: hidden;
    }
    .cell.disabled:hover {
        border: none;
        background-color: transparent;
    }
    .cell.other-month span {
        opacity: 0.4;
    }
    .cell.selected {
        color: var(--date-picker-selected-color, inherit);
        background: var(
            --date-picker-selected-background,
            rgba(2, 105, 247, 0.2)
        );
        border: 2px solid var(--date-picker-highlight-border, #0269f7);
    }
</style>
